<template>
  <teleport to="body">
    <div v-if="isOpen" :class="['fixed inset-0 overflow-y-auto', isTopMostModal ? 'z-[1100]' : 'z-[1000]']" @click.self="close">
      <div class="flex items-end justify-center h-screen min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <transition name="fade">
          <div v-if="isOpen" class="fixed inset-0 bg-gray-500 bg-opacity-50" />
        </transition>

        <!-- Centering trick -->
        <span class="inline-block h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

        <!-- Modal panel -->
        <transition name="scale-fade">
          <div
            v-if="isOpen"
            :class="[
              'inline-block w-full px-4 pt-5 pb-4 text-left align-top transition-all transform bg-white rounded-lg shadow-xl dark:bg-dark-box-background sm:my-8 sm:align-middle sm:p-6',
              maxWidth,
              isTopMostModal ? 'z-[1200]' : 'z-[1100]'
            ]">
            <div v-if="show_default_done_msg" class="flex justify-center">
              <transition name="tada">
                <div v-if="is_check_showing" class="absolute flex justify-center">
                  <CircleCheck class="w-20 h-20 text-green-400" aria-hidden="true" />
                </div>
              </transition>
              <div class="relative flex flex-col items-center justify-center h-full px-4 text-center">
                <h2 class="mt-20 text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
                  {{ defaultSuccessTitle }}
                </h2>
                <p class="mt-4 text-xl text-gray-500">
                  {{ defaultSuccessMsg }}
                </p>
                <div class="w-32 mt-5 sm:mt-6">
                  <button type="button" class="cancel-btn" @click="close">{{ $t("Done") }}</button>
                </div>
              </div>
            </div>
            <div v-else-if="show_is_done">
              <slot
                name="done"
                :close="close"
                :done="
                  () => {
                    show_is_done = true
                  }
                " />
            </div>
            <div v-else-if="show_error">
              <slot
                name="error"
                :close="close"
                :error="
                  () => {
                    show_error = true
                  }
                " />
            </div>
            <div v-else>
              <slot
                :close="close"
                :error="
                  () => {
                    show_error = true
                  }
                "
                :done="showDoneMsg" />
              <slot
                name="form"
                :close="close"
                :error="
                  () => {
                    show_error = true
                  }
                "
                :done="showDoneMsg" />
            </div>
          </div>
        </transition>
      </div>
    </div>
  </teleport>
</template>

<script>
import { CircleCheck } from "lucide-vue-next"
export default {
  components: {
    CircleCheck
  },
  props: {
    defaultSuccessTitle: {
      type: String,
      default: "Success"
    },
    defaultSuccessMsg: {
      type: String,
      default: ""
    },
    isOpen: {
      type: Boolean,
      required: false
    },
    useToast: {
      type: Boolean,
      required: false
    },
    maxWidth: {
      type: String,
      default: "max-w-[900px] mx-5"
    }
  },
  emits: ["update:isOpen", "beforeClose", "hide"],
  data() {
    return {
      modal_id: `modal-${Math.random().toString(36).substring(7)}`,
      show_is_done: false,
      show_error: false,
      show_default_done_msg: false,
      is_check_showing: false
    }
  },
  computed: {
    isTopMostModal() {
      return this.$store.state.ui.modal_stack.length && this.$store.state.ui.modal_stack.at(-1) === this.modal_id
    }
  },
  watch: {
    isOpen(val) {
      this.updateTopMost(val)
      if (val) {
        document.addEventListener("keydown", this.handleEscape)
      } else {
        document.removeEventListener("keydown", this.handleEscape)
      }
    },
    show_default_done_msg() {
      setTimeout(() => {
        this.is_check_showing = true
      }, 100)
    }
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscape)
  },
  methods: {
    handleEscape(e) {
      if (e.key === "Escape") {
        this.close()
      }
    },
    updateTopMost(isOpen) {
      if (isOpen) {
        this.$store.commit("ui/addModalToStack", this.modal_id)
      } else {
        this.$store.commit("ui/removeModalFromStack", this.modal_id)
      }
    },
    close() {
      if (this.isTopMostModal) {
        this.$emit("beforeClose")
        this.$emit("update:isOpen", false)
        setTimeout(() => {
          this.show_is_done = false
          this.show_error = false
          this.show_default_done_msg = false
        }, 300)
      }
    },

    showDoneMsg() {
      if (this.useToast) {
        this.showDefaultDoneMsg()
      } else {
        this.show_is_done = true
      }
    },
    showDefaultDoneMsg() {
      if (this.useToast) {
        this.close()
        this.$toast.open({
          message: `${this.defaultSuccessMsg}`,
          position: "top",
          type: "success",
          duration: 3000
        })
      } else {
        this.show_default_done_msg = true
        this.is_check_showing = false
      }
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.scale-fade-enter-active,
.scale-fade-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}
.scale-fade-enter,
.scale-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.tada-enter-active {
  animation: tada 1s;
}

@keyframes tada {
  0% {
    transform: scale(1);
  }
  10%,
  20% {
    transform: scale(0.9) rotate(-3deg);
  }
  30%,
  50%,
  70%,
  90% {
    transform: scale(1.1) rotate(3deg);
  }
  40%,
  60%,
  80% {
    transform: scale(1.1) rotate(-3deg);
  }
  100% {
    transform: scale(1) rotate(0);
  }
}
</style>
